import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface DragState {
  isDragging: boolean;
  draggedSquareIndex: number | null;
  draggedColor: string | null;
  currentX: number;
  currentY: number;
  offsetX: number;
  offsetY: number;
  snappedSquares: Record<number, string | null>; // Track which timeline squares have snapped items and their colors
  snappedSquareIndex: number | null; // Which timeline square the dragged item snapped to
}

interface DragStore extends DragState {
  // Actions
  startDrag: (
    squareIndex: number,
    color: string,
    x: number,
    y: number,
    offsetX: number,
    offsetY: number,
  ) => void;
  updateDragPosition: (x: number, y: number) => void;
  snapToOncall: (oncallSquareIndex: number) => void;
  resetDrag: () => void;
  registerTimelineSquarePosition: (
    squareIndex: number,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void;
  registerOncallSquarePosition: (
    squareIndex: number,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void;
  getTimelineSquarePosition: (
    squareIndex: number,
  ) => { x: number; y: number; width: number; height: number } | null;
  getOncallSquarePosition: (
    squareIndex: number,
  ) => { x: number; y: number; width: number; height: number } | null;
  resetToInitialState: () => void;
}

const initialState: DragState = {
  isDragging: false,
  draggedSquareIndex: null,
  draggedColor: null,
  currentX: 0,
  currentY: 0,
  offsetX: 0,
  offsetY: 0,
  snappedSquares: {},
  snappedSquareIndex: null,
};

const timelineSquarePositions: Record<
  number,
  { x: number; y: number; width: number; height: number }
> = {};

const oncallSquarePositions: Record<
  number,
  { x: number; y: number; width: number; height: number }
> = {};

export const useDragStore = create<DragStore>()(
  persist(
    (set) => ({
  ...initialState,

  startDrag: (squareIndex, color, x, y, offsetX, offsetY) =>
    set({
      isDragging: true,
      draggedSquareIndex: squareIndex,
      draggedColor: color,
      currentX: x,
      currentY: y,
      offsetX,
      offsetY,
    }),

  updateDragPosition: (x, y) =>
    set({
      currentX: x,
      currentY: y,
    }),

  snapToOncall: (oncallSquareIndex) =>
    set((state) => ({
      snappedSquares: {
        ...state.snappedSquares,
        [oncallSquareIndex]: state.draggedColor,
      },
      snappedSquareIndex: oncallSquareIndex,
      isDragging: false,
      draggedSquareIndex: null,
      draggedColor: null,
    })),

  resetDrag: () =>
    set((state) => ({
      isDragging: false,
      draggedSquareIndex: null,
      draggedColor: null,
      currentX: 0,
      currentY: 0,
      offsetX: 0,
      offsetY: 0,
      snappedSquareIndex: null,
      snappedSquares: state.snappedSquares,
    })),

  registerTimelineSquarePosition: (squareIndex, x, y, width, height) => {
    timelineSquarePositions[squareIndex] = { x, y, width, height };
  },

  registerOncallSquarePosition: (squareIndex, x, y, width, height) => {
    oncallSquarePositions[squareIndex] = { x, y, width, height };
  },

  getTimelineSquarePosition: (squareIndex) => {
    return timelineSquarePositions[squareIndex] || null;
  },

  getOncallSquarePosition: (squareIndex) => {
    return oncallSquarePositions[squareIndex] || null;
  },

  resetToInitialState: () =>
    set(initialState),
    }),
    {
      name: 'drag-store',
      partialize: (state) => ({
        snappedSquares: state.snappedSquares,
        snappedSquareIndex: state.snappedSquareIndex,
      }),
    },
  ),
);
